import {
  Alert, Button, Card, DatePicker, Form, Input, Modal, Select, Space, Table,
  Tabs, Tag, Typography, notification,
} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useCurrentRoles } from 'hooks/use-current-roles';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';

type Person = { id: number; name: string; enabled?: boolean; studioScope?: string };
type Route = { npcUserId: number; primaryTechId: number; backupTechId: number | null; managerId: number };
type Session = {
  id: number; npcUserId: number; npcName: string; location: string; startAt: string;
  endAt: string; status: string; liveShowId: number | null; primaryName?: string;
  backupName?: string; primaryTechOverrideId?: number | null;
  backupTechOverrideId?: number | null; managerId?: number | null;
  coverageWarning?: string | null;
};
type Request = {
  id: number; npcName: string; location: string; reasonCode: string; severity: string;
  status: string; targetName?: string; assignedName?: string; createdAt: string; note?: string;
};
type Meta = {
  npcs: Person[]; candidates: Person[]; technicians: Person[];
  managers: Person[]; routes: Route[];
  shifts: { id: number; name: string; startTime: string; endTime: string }[];
  reasons: Record<string, { label: string }>;
};

dayjs.extend(utc);
dayjs.extend(timezone);
const VN = 'Asia/Ho_Chi_Minh';
const vietnamIso = (d: Dayjs) => dayjs.tz(d.format('YYYY-MM-DD HH:mm'), 'YYYY-MM-DD HH:mm', VN).toISOString();

const labelOptions = (people: Person[]) => people.map((u) => ({ value: u.id, label: u.name || `#${u.id}` }));
const errorMessage = (error: any) => error?.error || error?.message || 'Không lưu được. Vui lòng thử lại.';
const fmt = (iso: string) => iso ? dayjs(iso).tz(VN).format('DD/MM HH:mm') : '—';

/** Tab nằm trong LIVE & Event → Liveshow; chỉ ADMIN sửa quyền/ghép người. */
const LiveSupportManager = () => {
  const { isAdmin } = useCurrentRoles();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [requests, setRequests] = useState<Request[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [date, setDate] = useState<Dayjs>(dayjs().tz(VN));
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState('requests');
  const [editing, setEditing] = useState<Session | null | undefined>(undefined);
  const [transferId, setTransferId] = useState<number | null>(null);
  const [transferTarget, setTransferTarget] = useState<number | undefined>();
  const [sessionForm] = Form.useForm();
  const [routeForm] = Form.useForm();
  const [shiftForm] = Form.useForm();
  const [technicianForm] = Form.useForm();

  const reload = useCallback(async () => {
    try {
      const [m, s, r]: any[] = await Promise.all([
        AxiosUtils.get(API.liveSupportAdmin + '/meta'),
        AxiosUtils.get(API.liveSupportAdmin + '/sessions'),
        AxiosUtils.get(API.liveSupportAdmin + '/requests'),
      ]);
      setMeta(m.data); setSessions(s.data); setRequests(r.data);
    } catch (e) { notification.error({ message: errorMessage(e) }); }
  }, []);
  const reloadAssignments = useCallback(async (day: Dayjs) => {
    try {
      const res: any = await AxiosUtils.get(API.liveSupportAdmin + '/shifts', { date: day.format('YYYY-MM-DD') });
      setAssignments(res.data);
    } catch (e) { notification.error({ message: errorMessage(e) }); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { reloadAssignments(date); }, [date, reloadAssignments]);
  useEffect(() => {
    if (tab !== 'requests') return;
    const timer = window.setInterval(() => {
      AxiosUtils.get<any, any>(API.liveSupportAdmin + '/requests')
        .then((r: any) => setRequests(r.data)).catch(() => undefined);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [tab]);

  const techs = useMemo(() => (meta?.technicians || []).filter((t) => t.enabled), [meta]);
  const techOptions = labelOptions(techs);
  const routeByNpc = useMemo(() => new Map(meta?.routes.map((r) => [r.npcUserId, r])), [meta]);
  const submit = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try { await action(); notification.success({ message }); await reload(); await reloadAssignments(date); return true; }
    catch (e) { notification.error({ message: errorMessage(e) }); return false; }
    finally { setBusy(false); }
  };

  const requestPanel = (
    <Card title="Chuông hỗ trợ LIVE" extra={<Button onClick={reload}>Làm mới</Button>}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Chỉ KTV được ghép và đang trong ca nhận chuông. Chưa ai nhận sẽ tự chuyển KTV dự phòng, rồi quản lý trực." />
      <Table rowKey="id" dataSource={requests} pagination={{ defaultPageSize: 15 }} scroll={{ x: 860 }}
        columns={[
          { title: 'NPC · Phòng', key: 'npc', render: (_: any, r: Request) => <><b>{r.npcName}</b><br />{r.location}</> },
          { title: 'Sự cố', key: 'reason', render: (_: any, r: Request) => <><Tag color={r.severity === 'URGENT' ? 'red' : 'blue'}>{meta?.reasons?.[r.reasonCode]?.label || r.reasonCode}</Tag><div>{r.note}</div></> },
          { title: 'Gửi lúc', dataIndex: 'createdAt', render: fmt },
          { title: 'Trạng thái', dataIndex: 'status', render: (s: string) => <Tag color={s === 'NEW' ? 'red' : s === 'RESOLVED' ? 'green' : 'gold'}>{({ NEW: 'Chưa ai nhận', ACKNOWLEDGED: 'Đã nhận', ARRIVED: 'Đã tới', RESOLVED: 'Đã xong', CANCELLED: 'Đã hủy' } as any)[s] || s}</Tag> },
          { title: 'Đang gọi / phụ trách', key: 'target', render: (_: any, r: Request) => r.assignedName || r.targetName || '—' },
          { title: '', key: 'action', render: (_: any, r: Request) => ['NEW', 'ACKNOWLEDGED', 'ARRIVED'].includes(r.status) ? <Button size="small" onClick={() => { setTransferTarget(undefined); setTransferId(r.id); }}>Chuyển KTV</Button> : null },
        ]} />
      <Modal open={transferId !== null} title="Chuyển cho KTV đang trực" onCancel={() => setTransferId(null)}
        okButtonProps={{ disabled: !transferTarget }} confirmLoading={busy} onOk={async () => {
          if (transferId && transferTarget && await submit(() => AxiosUtils.post(`${API.liveSupportAdmin}/requests/${transferId}/reassign`, { technicianId: transferTarget }), 'Đã chuyển KTV')) setTransferId(null);
        }}>
        <Select style={{ width: '100%' }} options={techOptions} value={transferTarget} placeholder="Chọn KTV" onChange={setTransferTarget} />
      </Modal>
    </Card>
  );

  const sessionPanel = (
    <Card title="Lịch LIVE NPC để gọi hỗ trợ" extra={<Button type="primary" onClick={() => {
      sessionForm.resetFields(); setEditing(null);
    }}>Xếp giờ LIVE</Button>}>
      <Typography.Paragraph type="secondary">Giờ hỗ trợ nội bộ có giờ kết thúc thật, không dùng thời lượng 15 phút của lịch Phòng live công khai.</Typography.Paragraph>
      <Table rowKey="id" dataSource={sessions} scroll={{ x: 820 }} pagination={{ defaultPageSize: 15 }} columns={[
        { title: 'NPC', dataIndex: 'npcName' },
        { title: 'Phòng', dataIndex: 'location' },
        { title: 'Bắt đầu', dataIndex: 'startAt', render: fmt },
        { title: 'Kết thúc', dataIndex: 'endAt', render: fmt },
        { title: 'KTV chính / dự phòng', key: 'technicians', render: (_: any, s: Session) => `${s.primaryName || 'Chưa ghép'} / ${s.backupName || 'Chưa có'}` },
        { title: 'Phủ ca', dataIndex: 'coverageWarning', render: (warning: string | null) => warning ? <Tag color="orange">{warning}</Tag> : <Tag color="green">Đủ ca</Tag> },
        { title: 'Trạng thái', dataIndex: 'status', render: (s: string) => <Tag color={s === 'CANCELLED' ? 'default' : 'blue'}>{s === 'CANCELLED' ? 'Đã hủy' : 'Đã xếp'}</Tag> },
        { title: '', key: 'action', render: (_: any, s: Session) => <Space>
          <Button size="small" onClick={() => {
            sessionForm.setFieldsValue({ npcUserId: s.npcUserId, location: s.location,
              startAt: dayjs(s.startAt).tz(VN), endAt: dayjs(s.endAt).tz(VN), liveShowId: s.liveShowId || undefined,
              primaryTechOverrideId: s.primaryTechOverrideId || undefined,
              backupTechOverrideId: s.backupTechOverrideId || undefined, managerId: s.managerId || undefined });
            setEditing(s);
          }}>Sửa</Button>
          {s.status !== 'CANCELLED' && <Button size="small" danger onClick={() => Modal.confirm({ title: 'Hủy lịch hỗ trợ?', content: 'Các yêu cầu đã gửi vẫn tiếp tục xử lý.', onOk: () => submit(async () => {
            const result: any = await AxiosUtils.post(`${API.liveSupportAdmin}/sessions/${s.id}/cancel`, {});
            if (result.data.openRequests) notification.warning({ message: `${result.data.openRequests} yêu cầu còn mở` });
          }, 'Đã hủy lịch') })}>Hủy</Button>}
        </Space> },
      ]} />
      <Modal open={editing !== undefined} title={editing?.id ? 'Sửa lịch NPC' : 'Xếp lịch NPC'}
        onCancel={() => setEditing(undefined)} onOk={() => sessionForm.submit()} confirmLoading={busy} okText="Lưu">
        <Form form={sessionForm} layout="vertical" onFinish={async (values) => {
          const payload = { npcUserId: values.npcUserId, location: values.location,
            startAt: vietnamIso(values.startAt), endAt: vietnamIso(values.endAt),
            liveShowId: values.liveShowId ? Number(values.liveShowId) : undefined,
            primaryTechOverrideId: values.primaryTechOverrideId || undefined,
            backupTechOverrideId: values.backupTechOverrideId || undefined,
            managerId: values.managerId || undefined };
          const saved = await submit(() => editing?.id
            ? AxiosUtils.patch(`${API.liveSupportAdmin}/sessions/${editing.id}`, payload)
            : AxiosUtils.post(`${API.liveSupportAdmin}/sessions`, payload), 'Đã lưu lịch LIVE NPC');
          if (saved) setEditing(undefined);
        }}>
          <Form.Item name="npcUserId" label="Creator NPC" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" options={labelOptions(meta?.npcs || [])} /></Form.Item>
          <Form.Item name="location" label="Phòng / tầng / vị trí" rules={[{ required: true }]}><Input maxLength={120} /></Form.Item>
          <Space align="start" wrap>
            <Form.Item name="startAt" label="Bắt đầu · giờ Việt Nam" rules={[{ required: true }]}><DatePicker showTime format="DD/MM/YYYY HH:mm" /></Form.Item>
            <Form.Item name="endAt" label="Kết thúc thật · giờ Việt Nam" rules={[{ required: true }]}><DatePicker showTime format="DD/MM/YYYY HH:mm" /></Form.Item>
          </Space>
          <Form.Item name="liveShowId" label="ID Phòng live công khai (nếu có)"><Input type="number" min={1} /></Form.Item>
          <Form.Item name="primaryTechOverrideId" label="KTV thay riêng buổi (mặc định dùng người ghép cố định)"><Select allowClear options={techOptions} /></Form.Item>
          <Form.Item name="backupTechOverrideId" label="Dự phòng thay riêng buổi"><Select allowClear options={techOptions} /></Form.Item>
          <Form.Item name="managerId" label="Quản lý trực thay riêng buổi"><Select allowClear options={labelOptions(meta?.managers || [])} /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );

  const routesPanel = (
    <Card title="Ghép NPC ↔ KTV" extra={!isAdmin && <Tag>Chỉ Admin được thay đổi</Tag>}>
      <Table rowKey="npcUserId" size="small" pagination={false} dataSource={meta?.routes || []} columns={[
        { title: 'NPC', dataIndex: 'npcUserId', render: (id: number) => meta?.npcs.find((n) => n.id === id)?.name || `#${id}` },
        { title: 'KTV chính', dataIndex: 'primaryTechId', render: (id: number) => techs.find((t) => t.id === id)?.name || `#${id}` },
        { title: 'Dự phòng', dataIndex: 'backupTechId', render: (id: number) => techs.find((t) => t.id === id)?.name || '—' },
        { title: 'Quản lý', dataIndex: 'managerId', render: (id: number) => meta?.managers.find((t) => t.id === id)?.name || `#${id}` },
        { title: '', render: (_: any, r: Route) => <Button size="small" disabled={!isAdmin} onClick={() => routeForm.setFieldsValue(r)}>Sửa</Button> },
      ]} />
      {isAdmin && <Form form={routeForm} layout="inline" style={{ marginTop: 20, gap: 8 }} onFinish={async (v) => {
        if (await submit(() => AxiosUtils.post(`${API.liveSupportAdmin}/routes`, v), 'Đã ghép KTV')) routeForm.resetFields();
      }}>
        <Form.Item name="npcUserId" rules={[{ required: true }]}><Select showSearch optionFilterProp="label" placeholder="NPC" style={{ width: 175 }} options={labelOptions(meta?.npcs || [])} onChange={(id) => routeForm.setFieldsValue(routeByNpc.get(id) || { primaryTechId: undefined, backupTechId: undefined, managerId: undefined })} /></Form.Item>
        <Form.Item name="primaryTechId" rules={[{ required: true }]}><Select placeholder="KTV chính" style={{ width: 150 }} options={techOptions} /></Form.Item>
        <Form.Item name="backupTechId"><Select allowClear placeholder="Dự phòng" style={{ width: 150 }} options={techOptions} /></Form.Item>
        <Form.Item name="managerId" rules={[{ required: true }]}><Select placeholder="Quản lý trực" style={{ width: 150 }} options={labelOptions(meta?.managers || [])} /></Form.Item>
        <Button type="primary" htmlType="submit" loading={busy}>Lưu ghép</Button>
      </Form>}
    </Card>
  );

  const shiftsPanel = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card title="KTV được phép nhận chuông">
        <Table rowKey="id" size="small" pagination={false} dataSource={meta?.technicians || []} columns={[
          { title: 'Tên', dataIndex: 'name' }, { title: 'Khu vực', dataIndex: 'studioScope', render: (x: string) => x || 'Tất cả' },
          { title: 'Trạng thái', dataIndex: 'enabled', render: (x: boolean) => x ? <Tag color="green">Được phép</Tag> : <Tag>Đã tắt</Tag> },
          { title: '', render: (_: any, t: Person) => <Button disabled={!isAdmin} size="small" onClick={() => technicianForm.setFieldsValue({ userId: t.id, enabled: !t.enabled, studioScope: t.studioScope })}>{t.enabled ? 'Tắt quyền' : 'Cấp lại'}</Button> },
        ]} />
        {isAdmin && <Form form={technicianForm} layout="inline" style={{ marginTop: 18, gap: 8 }} initialValues={{ enabled: true }} onFinish={async (v) => {
          if (await submit(() => AxiosUtils.post(`${API.liveSupportAdmin}/technicians`, v), 'Đã cập nhật quyền KTV')) technicianForm.resetFields();
        }}>
          <Form.Item name="userId" rules={[{ required: true }]}><Select placeholder="Nhân viên Kỹ thuật" style={{ width: 220 }} showSearch optionFilterProp="label" options={labelOptions([...(meta?.candidates || []), ...techs.filter(t => !(meta?.candidates || []).some(c => c.id === t.id))])} /></Form.Item>
          <Form.Item name="studioScope"><Input placeholder="Phòng / studio (tùy chọn)" style={{ width: 170 }} /></Form.Item>
          <Form.Item name="enabled" rules={[{ required: true }]}><Select style={{ width: 125 }} options={[{ value: true, label: 'Cấp quyền' }, { value: false, label: 'Tắt quyền' }]} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={busy}>Lưu KTV</Button>
        </Form>}
      </Card>
      <Card title="Xếp ca KTV trước giờ LIVE (ngày giờ Việt Nam)" extra={<DatePicker value={date} onChange={(d) => d && setDate(d)} />}>
        <Table size="small" pagination={false} rowKey="id" dataSource={assignments} columns={[
          { title: 'KTV', dataIndex: 'technicianName' }, { title: 'Ca', key: 'shift', render: (_: any, r: any) => `${r.shiftName} · ${r.startTime}–${r.endTime}` },
          { title: '', render: (_: any, r: any) => isAdmin && <Button danger size="small" onClick={() => Modal.confirm({ title: 'Bỏ ca KTV?', onOk: () => submit(() => AxiosUtils._delete(`${API.liveSupportAdmin}/shifts/${r.id}`, {}), 'Đã bỏ ca') })}>Bỏ</Button> },
        ]} />
        {isAdmin && <Form form={shiftForm} layout="inline" style={{ marginTop: 18, gap: 8 }} onFinish={async (v) => {
          if (await submit(() => AxiosUtils.post(`${API.liveSupportAdmin}/shifts`, { ...v, date: date.format('YYYY-MM-DD') }), 'Đã xếp ca')) shiftForm.resetFields();
        }}>
          <Form.Item name="technicianId" rules={[{ required: true }]}><Select placeholder="KTV" style={{ width: 175 }} options={techOptions} /></Form.Item>
          <Form.Item name="shiftId" rules={[{ required: true }]}><Select placeholder="Ca Kỹ thuật" style={{ width: 245 }} options={(meta?.shifts || []).map((s) => ({ value: s.id, label: `${s.name} · ${s.startTime}–${s.endTime}` }))} /></Form.Item>
          <Button type="primary" htmlType="submit" loading={busy}>Thêm ca</Button>
        </Form>}
      </Card>
    </Space>
  );

  return <Tabs activeKey={tab} onChange={setTab} items={[
    { key: 'requests', label: `Yêu cầu (${requests.filter(r => r.status === 'NEW').length} chờ)`, children: requestPanel },
    { key: 'sessions', label: 'Lịch LIVE NPC', children: sessionPanel },
    { key: 'routes', label: 'Ghép NPC - KTV', children: routesPanel },
    { key: 'shifts', label: 'KTV & ca trực', children: shiftsPanel },
  ]} />;
};

export default LiveSupportManager;
